import { CarePlanAuditPayload } from '../../api/carePlanAuditApi'
import { AuditReport, AuditSection } from './auditReportMock'

const pad = (n: number): string => String(n).padStart(2, '0')

const formatReviewedAt = (iso?: string): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return (
    `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}, ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  )
}

/** Coerce API list fields that may be strings or `{ text }` / `{ description }` objects. */
const asStringList = (value: unknown): string[] => {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed ? [trimmed] : []
  }
  if (!Array.isArray(value)) return []
  return value
    .map(item => {
      if (typeof item === 'string') return item.trim()
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>
        const text = obj.text ?? obj.description ?? obj.message ?? obj.value
        if (typeof text === 'string') return text.trim()
      }
      return ''
    })
    .filter(Boolean)
}

const mapStandard = (raw: unknown, index: number): AuditSection => {
  const s = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const title =
    (typeof s.title === 'string' && s.title) ||
    (typeof s.name === 'string' && s.name) ||
    (typeof s.standard === 'string' && s.standard) ||
    (typeof s.label === 'string' && s.label) ||
    `Standard ${index + 1}`

  return {
    title,
    strengths: asStringList(s.strengths ?? s.strength),
    recommendations: asStringList(
      s.recommendations ?? s.recommendationsForImprovement ?? s.improvements,
    ),
  }
}

/**
 * Turns the real care-plan-audit `data` envelope into the report shape the
 * AuditResult screen already renders. Does not invent section copy — empty
 * arrays stay empty when the API omits them.
 *
 * `fallbackPatientName` is the table-provided name when the API omits
 * firstName/lastName on the patient object.
 */
export const mapCarePlanAuditToReport = (
  payload: CarePlanAuditPayload,
  filename: string,
  fallbackPatientName?: string,
): AuditReport => {
  const { patient, current } = payload
  const patientName =
    `${patient.firstName || ''} ${patient.lastName || ''}`.trim() ||
    (fallbackPatientName || '').trim() ||
    'Service user'

  if (!current) {
    const fromPatientPlan = patient.carePlans?.[0]?.originalFilename
    return {
      patientName,
      filename: filename || fromPatientPlan || 'care-plan.pdf',
      reviewedAt: '—',
      reviewer: 'QCS AI Auditor',
      cqcAlignment: 0,
      summary: '',
      sections: [],
      inconsistencies: [],
    }
  }

  const alignmentRaw = current.cqcAlignment ?? current.alignment
  const cqcAlignment =
    typeof alignmentRaw === 'number' && !Number.isNaN(alignmentRaw)
      ? Math.max(0, Math.min(100, Math.round(alignmentRaw)))
      : 0

  // Only use API-provided summary — do not invent copy when the field is absent.
  const summary = typeof current.summary === 'string' ? current.summary : ''

  const reviewer =
    (typeof current.reviewer === 'string' && current.reviewer) || 'QCS AI Auditor'

  const fromPatientPlan = patient.carePlans?.[0]?.originalFilename
  const resolvedFilename = filename || fromPatientPlan || 'care-plan.pdf'

  return {
    patientName,
    filename: resolvedFilename,
    reviewedAt: formatReviewedAt(current.createdAt),
    reviewer,
    cqcAlignment,
    summary,
    sections: (current.standards || []).map(mapStandard),
    inconsistencies: asStringList(current.inconsistencies),
  }
}
